import { generatePath } from "react-router-dom";
import { emailRegex } from "./constants";
import { frontendRoutes } from "./frontendRoutes";

export const getRedirectRouteWithFallback = (location) => {
  return getRedirectRoute(location) || frontendRoutes.HOME;
};

export const getRedirectRoute = (location) => {
  if (location.state?.from?.pathname) {
    return `${location.state?.from?.pathname}${location.state?.from?.search}`;
  }
};

// Builds a /search URL pre-filtered by category/delivery/payment method —
// mirrors the mobile app's "tap a category/delivery/payment chip on a
// product or shop → jump into Search pre-filtered" pattern.
export const buildSearchFilterPath = ({
  category,
  categories,
  deliveryMethods,
  paymentMethods,
} = {}) => {
  const params = new URLSearchParams();
  if (categories?.length) params.set("categories", categories.join(","));
  else if (category) params.set("category", category);
  if (deliveryMethods?.length)
    params.set("deliveryMethods", deliveryMethods.join(","));
  if (paymentMethods?.length)
    params.set("paymentMethods", paymentMethods.join(","));
  return `${frontendRoutes.SEARCH}?${params.toString()}`;
};

export const isValidEmail = (inputString) => {
  return emailRegex.test(inputString);
};

export const isNumeric = (inputString) => {
  return /^\d+$/.test(inputString);
};

export const isNumericWithPlus = (inputString) => {
  return /^\+?\d+$/.test(inputString);
};

const currencySymbols = {
  NGN: "₦",
  USD: "$",
  EUR: "€",
  GBP: "£",
  CAD: "$",
  AUD: "$",
  GHS: "₵",
  KES: "KSh",
  ZAR: "R",
  JPY: "¥",
  CNY: "¥",
  XOF: "CFA",
};

const COUNTRY_TO_CURRENCY = {
  NG: "NGN",
  US: "USD",
  GB: "GBP",
  GH: "GHS",
  KE: "KES",
  BJ: "XOF",
  CA: "CAD",
  AU: "AUD",
  ZA: "ZAR",
};

export const getCurrencyForCountry = (countryCode) => {
  if (!countryCode) return "NGN";
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || "NGN";
};

// Unlike getCurrencyForCountry, no NGN default: an unknown country has no home
// currency, rather than showing naira to someone who isn't in Nigeria.
export const getHomeCurrencyForCountry = (countryCode) =>
  COUNTRY_TO_CURRENCY[countryCode?.toUpperCase()] ?? null;

const currencyNames = {
  NGN: "Nigerian Naira",
  USD: "US Dollar",
  EUR: "Euro",
  GBP: "British Pound",
  CAD: "Canadian Dollar",
  AUD: "Australian Dollar",
  GHS: "Ghanaian Cedi",
  KES: "Kenyan Shilling",
  ZAR: "South African Rand",
  XOF: "West African CFA Franc",
};

// "Nigerian Naira (NGN)", or null for an unknown country. Mirrors the mobile
// app's getCurrencyLabelForCountry.
export const getCurrencyLabelForCountry = (countryCode) => {
  const currencyCode = getHomeCurrencyForCountry(countryCode);
  if (!currencyCode) return null;
  const name = currencyNames[currencyCode];
  return name ? `${name} (${currencyCode})` : currencyCode;
};

export const resolveCurrencySymbol = (currency) => {
  if (!currency) return "₦";
  const upperCurrency = `${currency}`.trim().toUpperCase();
  return currencySymbols[upperCurrency] || "₦";
};

export const formatCurrency = (amount, currency = "NGN") => {
  const symbol = resolveCurrencySymbol(currency);
  const numericAmount = Number(amount);
  const formattedAmount = Number.isFinite(numericAmount)
    ? numericAmount.toLocaleString()
    : (amount ?? "0");
  return `${symbol}${formattedAmount}`;
};

// Use for product/unit price displays specifically — a price of exactly 0 reads as "Free"
// rather than "₦0". Not for totals/subtotals, which can legitimately be non-price sums.
export const formatProductPrice = (amount, currency = "NGN") => {
  const numericAmount = Number(amount);
  if (numericAmount === 0) return "Free";
  return formatCurrency(amount, currency);
};

// rates: USD value of 1 unit per currency. Null when either currency is unknown.
export const convertPrice = (amount, from, to, rates) => {
  const fromRate = rates?.[`${from}`.toUpperCase()];
  const toRate = rates?.[`${to}`.toUpperCase()];
  if (!fromRate || !toRate) return null;
  return (Number(amount) * fromRate) / toRate;
};

// "≈ ₦67,400", rounded to 3 significant figures because a daily mid-market rate
// can't honestly promise more. Null when there's nothing useful to show: same
// currency, a free or missing price, or no rate. Mirrors the mobile app.
export const formatConvertedPrice = ({
  amount,
  maxAmount,
  from,
  to,
  rates,
}) => {
  if (!from || !to || !(Number(amount) > 0)) return null;
  if (`${from}`.toUpperCase() === `${to}`.toUpperCase()) return null;
  const format = (value) => {
    const converted = convertPrice(value, from, to, rates);
    return converted == null
      ? null
      : formatCurrency(Number(converted.toPrecision(3)), to);
  };
  const low = format(amount);
  if (!low) return null;
  return Number(maxAmount) > Number(amount)
    ? `≈ ${low} – ${format(maxAmount)}`
    : `≈ ${low}`;
};

// One estimate for totals from sumByCurrency, so a mixed-currency cart can still
// show roughly what it adds up to. Null if any currency has no rate, rather than
// an estimate that quietly leaves part of the cart out. Mirrors the mobile app.
export const formatConvertedTotals = ({ totals, to, rates }) => {
  const shown = (totals || []).filter((t) => t.amount > 0);
  if (shown.length <= 1) {
    return formatConvertedPrice({
      amount: shown[0]?.amount,
      from: shown[0]?.currency,
      to,
      rates,
    });
  }
  if (!to) return null;
  const converted = shown.map((t) =>
    convertPrice(t.amount, t.currency, to, rates),
  );
  if (converted.includes(null)) return null;
  const sum = converted.reduce((total, value) => total + value, 0);
  return `≈ ${formatCurrency(Number(sum.toPrecision(3)), to)}`;
};

// The prices a buyer can see on a product, as ConvertedPrice props. A hidden
// price (OFFERS) has none.
export const getProductPriceBounds = (product) => {
  if (!product || isPriceHidden(product)) return { amount: null };
  const price =
    product.price ??
    (product.priceCents != null ? product.priceCents / 100 : null);
  const minPrice =
    product.minPrice ??
    (product.minPriceCents != null ? product.minPriceCents / 100 : null);
  return product.pricingMode === "RANGE" && minPrice != null
    ? { amount: minPrice, maxAmount: price }
    : { amount: price };
};

// A product's buyer-facing price: "₦10,000", or "₦15,000 – ₦30,000" for a
// price range (always shown in full, never "From ₦15,000"). Mirrors the
// mobile app's formatProductPriceLabel. minPrice is a Product virtual; raw
// sources (e.g. aggregations) may only carry minPriceCents.
// A hidden price product (OFFERS) has no price to show.
export const formatProductPriceLabel = (product) => {
  if (!product) return "";
  if (isPriceHidden(product)) return "Open to offers";
  const minPrice =
    product.minPrice ??
    (product.minPriceCents != null ? product.minPriceCents / 100 : null);
  if (product.pricingMode === "RANGE" && minPrice != null) {
    return `${formatCurrency(minPrice, product.currency)} – ${formatCurrency(product.price, product.currency)}`;
  }
  return formatProductPrice(product.price, product.currency);
};

// RANGE and OFFERS products are always negotiable; FIXED ones only when the
// seller opted in.
export const isProductNegotiable = (product) =>
  product?.pricingMode === "RANGE" ||
  product?.pricingMode === "OFFERS" ||
  product?.negotiable === true;

// OFFERS: the seller's asking price is hidden from buyers (the API never
// sends it to them), so the buyer has to make an offer. Admin reads include it.
export const isPriceHidden = (product) => product?.pricingMode === "OFFERS";

// Unit price a buyer is proposing for a cart line: their offer (made in the
// app) if any, else the listed price. Major units, like product.price.
export const getEffectiveUnitPrice = (item, product = item?.product) =>
  item?.offerPriceCents != null
    ? item.offerPriceCents / 100
    : Number(product?.price) || 0;

// Unit price for a cart line's "Listed" total: the listed price, except on a
// hidden price product, which has none, so its offer counts on both lines.
export const getListedUnitPrice = (item, product = item?.product) =>
  isPriceHidden(product)
    ? getEffectiveUnitPrice(item, product)
    : Number(product?.price) || 0;

// Totals for a list of cart items, kept separate per currency: a cart can hold
// products in different currencies, so it must never add naira to pounds and
// label the sum with one symbol. Returns [{ currency, amount }] in first-seen
// order. Mirrors the mobile app's sumByCurrency.
export const sumByCurrency = (items = [], getAmount) => {
  const totals = new Map();
  for (const item of items || []) {
    const currency = item?.product?.currency || "NGN";
    const amount = Number(getAmount(item)) || 0;
    totals.set(currency, (totals.get(currency) || 0) + amount);
  }
  return [...totals].map(([currency, amount]) => ({ currency, amount }));
};

// "₦5,000" or "₦5,000 + £20". Mirrors the mobile app's formatCurrencyTotals.
export const formatCurrencyTotals = (totals = []) => {
  const nonZero = totals.filter((t) => t.amount !== 0);
  const shown = nonZero.length ? nonZero : totals.slice(0, 1);
  if (!shown.length) return formatCurrency(0, "NGN");
  return shown.map((t) => formatCurrency(t.amount, t.currency)).join(" + ");
};

// Product and User pages resolve either a raw ObjectId or their slug at the
// same backend route (see getProductForCustomers/getPublicUserProfile's
// OBJECT_ID_PATTERN check), so the frontend can freely prefer the slug —
// it's a pure readability/SEO win with no separate URL shape to track.
export const buildProductPath = (product) =>
  generatePath(frontendRoutes.VIEW_PRODUCT, {
    productId: product?.slug || product?.id || product?._id,
  });

export const buildUserPath = (user) =>
  generatePath(frontendRoutes.VIEW_USER, {
    userId: user?.slug || user?.id || user?._id,
  });

// Shop is the one entity where slug-vs-id is a genuinely different URL
// shape, not just a different value at the same param: a handle needs the
// literal "@" prefix ShopPage.jsx checks for, since "/shop/@:handle" and
// "/shop/:shopId" are two separate backend routes with no fallback between
// them (unlike Product/User's single-route resolver) — and unlike Product's
// slug, a shop's handle is optional and can be null.
//
// generatePath percent-encodes the "@" to "%40", which would end up in copied
// share links. "@" is legal in a URL path, so put it back for readability.
export const buildShopPath = (shop) =>
  generatePath(frontendRoutes.VIEW_SHOP, {
    shopId: shop?.handle ? `@${shop.handle}` : shop?.id || shop?._id,
  }).replace("%40", "@");

export const getProductAvailableStock = (product) => {
  if (!product || product.stockQuantity == null) return null;
  return Math.max(0, product.stockQuantity - (product.reservedQuantity || 0));
};

export const isProductOutOfStock = (product) => {
  if (!product) return false;
  if (product.status === "OUT_OF_STOCK") return true;
  const availableStock = getProductAvailableStock(product);
  return availableStock !== null && availableStock === 0;
};
